import { Injectable, OnDestroy } from '@angular/core';
import { SwPush } from '@angular/service-worker';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { baseUrl } from "../service/global.service";
import { FirebaseFcmService } from "../../firebase/firebase-fcm.service";
import { PushDeviceService } from "../../shared/services/push-device.service";
import { environment } from "src/environments/environment";

/** User-facing push permission banner: enabled, prompt, or blocked. */
export type NotifBannerState = 'granted' | 'request' | 'denied' | null;

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  readonly VAPID_PUBLIC_KEY = 'BGNH8e-EiV9V7B9jjZuV2askzGselY6-QMPiYP_s814JAff5sydmi6aB6Ce6RdtrAJhdGwDd03ngKZ0QlkckvXI';

  private static readonly DISMISS_KEY = 'notif_banner_dismissed_for';

  /** Drives the fixed in-app notification status banner. */
  readonly bannerState$ = new BehaviorSubject<NotifBannerState>(null);

  private pendingUserId: number | null = null;

  constructor(
    private swPush: SwPush,
    private http: HttpClient,
    private firebaseFcm: FirebaseFcmService,
    private pushDevice: PushDeviceService,
  ) {}

  private useFirebaseWebPush(): boolean {
    return !!environment.firebaseWebPushVapidKey?.trim();
  }

  private getBrowserPermission(): NotificationPermission | null {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      return null;
    }
    return Notification.permission;
  }

  private getDismissedFor(): string | null {
    try {
      return localStorage.getItem(NotificationService.DISMISS_KEY);
    } catch {
      return null;
    }
  }

  private setDismissedFor(permission: string): void {
    try {
      localStorage.setItem(NotificationService.DISMISS_KEY, permission);
    } catch {
      /* ignore */
    }
  }

  /** Hide banner until browser permission changes (user dismissed for current state). */
  private shouldShowBanner(permission: NotificationPermission): boolean {
    return this.getDismissedFor() !== permission;
  }

  /** Map browser permission → banner state and publish (respects dismiss). */
  syncBannerState(): void {
    const permission = this.getBrowserPermission();
    if (!permission || !this.shouldShowBanner(permission)) {
      this.bannerState$.next(null);
      return;
    }
    if (permission === 'granted') {
      this.bannerState$.next('granted');
    } else if (permission === 'default') {
      this.bannerState$.next('request');
    } else {
      this.bannerState$.next('denied');
    }
  }

  /** Re-check after user returns from browser settings. */
  refreshBannerState(): void {
    if (this.pendingUserId == null) {
      return;
    }
    if (this.useFirebaseWebPush()) {
      void this.pushDevice.syncTokenIfEnabled();
    }
    this.syncBannerState();
  }

  checkPermissionAndRequest(userId: number) {
    this.pendingUserId = userId;

    const permission = this.getBrowserPermission();
    if (!permission) {
      this.bannerState$.next(null);
      return;
    }

    if (this.useFirebaseWebPush()) {
      void this.pushDevice.initAfterLogin({
        syncToken: permission === 'granted',
      });
    } else if (permission === 'granted') {
      this.checkAndUpdateSubscription(userId);
    }

    this.syncBannerState();
  }

  async grantPermission() {
    if (this.useFirebaseWebPush()) {
      await this.pushDevice.enableForCurrentDevice();
    } else if (this.pendingUserId != null) {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        this.checkAndUpdateSubscription(this.pendingUserId);
      }
    }
    this.syncBannerState();
  }

  dismissBanner() {
    const permission = this.getBrowserPermission();
    if (permission) {
      this.setDismissedFor(permission);
    }
    this.bannerState$.next(null);
  }

  async checkAndUpdateSubscription(userId: number) {
    this.swPush.subscription.subscribe(async subscription => {
      if (subscription) {
        const storedSubscriptions = await this.getStoredSubscriptionFromBackend(userId);
        const exists = storedSubscriptions?.some(
          (sub: any) => JSON.stringify(sub) === JSON.stringify(subscription)
        );
        if (!exists) {
           this.sendSubscriptionToBackend(userId, subscription);
        }
      } else {
         this.requestSubscription(userId);
      }
    });
  }

  requestSubscription(userId: number) {
    this.swPush.requestSubscription({
      serverPublicKey: this.VAPID_PUBLIC_KEY
    })
    .then(subscription => {
       this.sendSubscriptionToBackend(userId, subscription);
    })
    .catch(err => console.error("Could not subscribe to notifications", err));
  }

  getStoredSubscriptionFromBackend(userId: number): Promise<PushSubscription[] | null> {
    return this.http.get<PushSubscription[] | null>(`${baseUrl}users/${userId}/subscription`)
      .toPromise()
      ?.catch(err => {
        console?.error('Error fetching stored subscriptions:', err);
        return null;
      });
  }

  sendSubscriptionToBackend(userId: number, subscription: PushSubscription) {
    this.http.post(`${baseUrl}users/${userId}/subscribe`, subscription).subscribe(
      () => undefined,
      error => console.error('Error saving subscription:', error)
    );
  }
}
